import { z } from "zod";
import { uuid } from "./common";

/*
 * Ayurveda edition A3: therapy management. A therapy menu, therapists with shifts and leave, courses prescribed for
 * a number of days, and the sessions the scheduler books (table + therapist(s), same-gender rule), recorded with
 * start / finish, oil used and how the patient responded.
 */

export const THERAPY_CATEGORIES = ["snehana", "swedana", "panchakarma", "local", "other"] as const;
export type TherapyCategory = (typeof THERAPY_CATEGORIES)[number];
export const THERAPY_CATEGORY_LABEL: Record<TherapyCategory, string> = {
  snehana: "Snehana (oleation)",
  swedana: "Swedana (sudation)",
  panchakarma: "Panchakarma",
  local: "Local (Basti, Dhara…)",
  other: "Other",
};

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time like 07:30");
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-10-12");
const optText = (max: number) => z.string().trim().max(max).nullish().transform((v) => v || null);

export const therapyTypeInput = z
  .object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9][A-Z0-9_-]*$/).max(20),
    name: z.string().trim().min(1).max(120),
    category: z.enum(THERAPY_CATEGORIES).default("other"),
    durationMinutes: z.number().int().min(5).max(240),
    therapistsNeeded: z.number().int().min(1).max(4).default(1),
    serviceId: uuid.nullish().transform((v) => v ?? null),
    oilItemId: uuid.nullish().transform((v) => v ?? null),
    oilQuantity: z.number().positive().max(100000).nullish().transform((v) => v ?? null),
    notes: optText(1000),
    isActive: z.boolean().default(true),
  })
  .refine((t) => (t.oilItemId === null) === (t.oilQuantity === null), { path: ["oilQuantity"], message: "Give both the oil and how much is used" });
export type TherapyTypeInput = z.input<typeof therapyTypeInput>;

export interface TherapyType {
  id: string;
  code: string;
  name: string;
  category: TherapyCategory;
  durationMinutes: number;
  therapistsNeeded: number;
  serviceId: string | null;
  serviceName: string | null;
  oilItemId: string | null;
  oilItemName: string | null;
  oilUnit: string | null;
  oilQuantity: number | null;
  notes: string | null;
  isActive: boolean;
}

/* ------------------------------------------------------------------ therapists */

export const therapistUpdateInput = z.object({
  isTherapist: z.boolean(),
  gender: z.enum(["female", "male", "other", "undisclosed"]).nullish(),
});
export const shiftsInput = z.object({
  shifts: z
    .array(z.object({ branchId: uuid, weekday: z.number().int().min(1).max(7), startTime: hhmm, endTime: hhmm }).refine((s) => s.endTime > s.startTime, { path: ["endTime"], message: "Ends after it starts" }))
    .max(40),
});
export const leaveInput = z.object({ startsOn: isoDate, endsOn: isoDate, reason: optText(200) }).refine((l) => l.endsOn >= l.startsOn, { path: ["endsOn"], message: "Ends on or after the first day" });

export interface TherapistView {
  staffId: string;
  fullName: string;
  gender: string | null;
  isTherapist: boolean;
  branchIds: string[];
  shifts: { id: string; branchId: string; weekday: number; startTime: string; endTime: string }[];
  leaves: { id: string; startsOn: string; endsOn: string; reason: string | null }[];
}

/* ------------------------------------------------------------------ courses and sessions */

export const createCourseInput = z.object({
  branchId: uuid,
  title: z.string().trim().min(1).max(120),
  startDate: isoDate,
  days: z.number().int().min(1).max(60),
  preferredStart: hhmm.nullish().transform((v) => v ?? null),
  sameGender: z.boolean().default(true),
  visitId: uuid.nullish().transform((v) => v ?? null),
  notes: optText(2000),
  items: z
    .array(z.object({ therapyTypeId: uuid, dayFrom: z.number().int().min(1).max(60).nullish(), dayTo: z.number().int().min(1).max(60).nullish(), notes: optText(500) }))
    .min(1)
    .max(8),
});
export type CreateCourseInput = z.input<typeof createCourseInput>;

export const cancelCourseInput = z.object({ reason: z.string().trim().min(3).max(300) });

export const SESSION_STATUSES = ["scheduled", "in_progress", "completed", "missed", "cancelled"] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];
export const PATIENT_RESPONSES = ["better", "same", "worse", "discomfort"] as const;
export type PatientResponse = (typeof PATIENT_RESPONSES)[number];
export const PATIENT_RESPONSE_LABEL: Record<PatientResponse, string> = { better: "Feeling better", same: "No change", worse: "Worse", discomfort: "Discomfort during therapy" };

export interface TherapySession {
  id: string;
  courseId: string;
  courseTitle: string;
  patientId: string;
  patientName: string;
  patientCode: string;
  patientGender: string | null;
  therapyTypeId: string;
  therapyName: string;
  dayNo: number;
  courseDays: number;
  branchId: string;
  roomId: string | null;
  roomName: string | null;
  chairId: string | null;
  chairName: string | null;
  startsAt: string;
  endsAt: string;
  status: SessionStatus;
  staff: { id: string; fullName: string; gender: string | null }[];
  actualMinutes: number | null;
  patientResponse: PatientResponse | null;
  notes: string | null;
  missedReason: string | null;
  consumption: { itemId: string; itemName: string; quantity: number; unit: string }[];
  /** The patient package this session was taken from (A4), if any. */
  packageName: string | null;
  version: number;
}

export interface TherapyCourse {
  id: string;
  patientId: string;
  branchId: string;
  title: string;
  startDate: string;
  days: number;
  preferredStart: string | null;
  sameGender: boolean;
  status: "active" | "completed" | "cancelled";
  cancelReason: string | null;
  doctorName: string | null;
  notes: string | null;
  items: { id: string; therapyTypeId: string; therapyName: string; sequence: number; dayFrom: number; dayTo: number }[];
  counts: { total: number; scheduled: number; completed: number; missed: number; cancelled: number };
}

export interface UnscheduledSlot {
  dayNo: number;
  date: string;
  therapyName: string;
  reason: "closed" | "no_table" | "no_therapist";
}
export const UNSCHEDULED_REASON: Record<UnscheduledSlot["reason"], string> = {
  closed: "Centre closed that day",
  no_table: "No free therapy table in a suitable room",
  no_therapist: "Not enough free therapists (of the right gender) on shift",
};

export interface CourseResult {
  course: TherapyCourse;
  sessions: TherapySession[];
  unscheduled: UnscheduledSlot[];
}

export const completeSessionInput = z.object({
  version: z.number().int().min(1),
  actualMinutes: z.number().int().min(1).max(480).nullish().transform((v) => v ?? null),
  patientResponse: z.enum(PATIENT_RESPONSES).nullish().transform((v) => v ?? null),
  notes: optText(2000),
  consumption: z.array(z.object({ inventoryItemId: uuid, quantity: z.number().positive().max(100000) })).max(10).default([]),
});
export const missedSessionInput = z.object({ version: z.number().int().min(1), reason: z.string().trim().min(2).max(300), reschedule: z.boolean().default(true) });
export const startSessionInput = z.object({ version: z.number().int().min(1) });
export const assignSessionInput = z.object({
  version: z.number().int().min(1),
  staffIds: z.array(uuid).min(1).max(4).optional(),
  chairId: uuid.optional(),
  startsAt: z.iso.datetime({ offset: true }).optional(),
});

export const boardQuery = z.object({ branchId: uuid, date: isoDate });
export interface TherapyBoard {
  date: string;
  branchId: string;
  timeZone: string;
  rooms: { id: string; name: string; gender: "any" | "male" | "female"; chairs: { id: string; name: string }[] }[];
  therapists: { id: string; fullName: string; gender: string | null; shifts: { start: string; end: string }[]; onLeave: boolean }[];
  sessions: TherapySession[];
}
export const mySessionsQuery = z.object({ date: isoDate.optional() });
